import { IS_SANDBOX_ENV } from 'helper/constant';

import { ReactComponent as BillingIcon } from 'asset/CustomIcons/banking-badge.svg';
import { ReactComponent as BugIcon } from 'asset/CustomIcons/bug.svg';
import { ReactComponent as DiamondIcon } from 'asset/CustomIcons/diamond.svg';
import { ReactComponent as SearchNoteIcon } from 'asset/CustomIcons/magnifier.svg';
import { ReactComponent as NotesIcon } from 'asset/CustomIcons/notes.svg';
import { ReactComponent as RocketIcon } from 'asset/CustomIcons/rocket.svg';
import { ReactComponent as UsersIcon } from 'asset/CustomIcons/users.svg';
import { ReactComponent as ConnectIcon } from 'asset/IconsPack/at_link-03.svg';
import { ReactComponent as MailIcon } from 'asset/IconsPack/at_mail-01.svg';
import { ReactComponent as ShareIcon } from 'asset/IconsPack/at_share-03.svg';
import { ReactComponent as ShieldIcon } from 'asset/IconsPack/at_shield-tick.svg';

const commonSetupContent = {
    inviteTeam: (
        <>
            Invite key team members who will help manage your account — such as someone responsible
            for signing the agreement, handling billing, or setting up development and integrations.
            You can assign them specific roles to help them get started quickly and collaborate
            efficiently.
            <ul>
                <li>
                    <b>Owner:</b> Has full control over the account. Can invite and remove users,
                    manage billing, view all data, and edit workflows. Each account has one Owner.
                </li>
                <li>
                    <b>Admin:</b> Can manage workflows, view data, invite team members, and
                    configure most account settings — but cannot access billing.
                </li>
                <li>
                    <b>Viewer:</b> Has read-only access. Ideal for team members who need to observe
                    workflows, review results, or download reports without making changes.
                </li>
            </ul>
        </>
    ),
    tryOutProducts: (
        <>
            Get a look and feel of the platform with your own test environment. Every sandbox
            account comes with <b>$100</b> in free testing credits valid for 1 month to help you
            simulate real scenarios without any risk. Your Home screen will walk you through each
            step needed to get started, making setup easy and clear from the moment you log in.
        </>
    ),
    workflowSetup: (
        <>
            Based on your verification needs, you can configure a workflow that aligns with your
            internal processes. You’ll be able to select checks such as identity verification,
            criminal background screening, or continuous monitoring — all customized to fit your
            business logic.
            <br />
            <video
                src="https://cdn.authenticating.com/public/portal/video-guides/Medallion%E2%84%A2+Workflow+Demo.mp4"
                loop
                playsInline
                controls
                controlsList="nodownload"
                disablePictureInPicture
                disableRemotePlayback
            />
        </>
    ),
    integrationOptions: [
        {
            id: 1,
            content: (
                <>
                    <b>API</b> – Build a fully customized verification flow using Authenticate’s
                    robust API. Best suited for technical teams looking to deeply integrate with
                    their own platforms.
                </>
            ),
            button: {
                text: 'API Docs',
                link: IS_SANDBOX_ENV
                    ? 'https://info.authenticate.com/sandbox'
                    : 'https://docs.authenticate.com/',
                linkIcon: ShareIcon,
                external: true,
            },
        },
        {
            id: 2,
            content: (
                <>
                    <b>Low Code SDK</b> – Use our SDKs to implement key functionality with minimal
                    code. A great choice for teams that want a faster path to integration without
                    building from scratch.
                </>
            ),
            button: {
                text: 'Low Code SDK',
                link: '/settings#credentials',
                linkIcon: ShareIcon,
            },
        },
        {
            id: 3,
            content: (
                <>
                    <b>Partner Integrations</b> – (Guesty, Hostaway, Lodgify, Hostfully, OwnerRez)
                    Connect Authenticate directly with supported property management systems. No dev
                    work required — these integrations allow for seamless setup.
                </>
            ),
            button: {
                text: 'Integrations',
                link: '/integrations',
            },
        },
        {
            id: 4,
            content: (
                <>
                    <b>Manual Links</b> – Generate one-off verification links that you can send to
                    users over email. A simple way to start verifying users without writing any
                    code.
                </>
            ),
            button: {
                text: 'Manage Users',
                link: '/users',
            },
        },
    ],
};

export const PROD_SETUP_STEPS = {
    GET_STARTED: {
        id: 1,
        title: 'Get Set Up',
        Icon: NotesIcon,
        duration: 'About 3 mins',
        steps: [
            {
                id: 1,
                completed: false,
                title: 'Review Agreement',
            },
            {
                id: 2,
                title: 'Invite Your Team',
                Icon: UsersIcon,
                content: commonSetupContent.inviteTeam,
                button: {
                    text: 'Invite Your Team',
                    link: '/settings#team',
                },
                completed: false,
            },
        ],
    },
    TRY_OUT_PRODUCTS: {
        id: 2,
        title: 'Try Out Products',
        Icon: DiamondIcon,
        duration: 'About 8 mins',
        steps: [
            {
                id: 1,
                title: 'Test How It Works',
                Icon: BugIcon,
                content: commonSetupContent.tryOutProducts,
                button: {
                    text: 'Request Sandbox',
                    link: '#',
                    isOnClickHandler: true,
                },
            },
            {
                id: 2,
                title: 'Build a Workflow (or Start with a Template)',
                Icon: ShieldIcon,
                content: commonSetupContent.workflowSetup,
                button: {
                    text: 'Create A Workflow',
                    link: '/workflows',
                },
                completed: false,
            },
        ],
    },
    GO_LIVE_READINESS: {
        id: 3,
        title: 'Go Live Readiness',
        Icon: RocketIcon,
        duration: 'About 8 mins',
        steps: [
            {
                id: 1,
                title: 'Set Up Billing',
                Icon: BillingIcon,
                content:
                    'Securely add your preferred payment method and top-up your balance to enable services. This is a prerequisite for accessing credentials and running verifications.',
                button: {
                    text: 'Set Up Billing',
                    link: '/billing/dashboard',
                    disabled: false,
                },
                completed: false,
            },
            {
                id: 2,
                title: 'Connect to Your App',
                Icon: ConnectIcon,
                content: 'Choose how you’d like to integrate:',
                subContent: commonSetupContent.integrationOptions,
            },
            {
                id: 3,
                title: 'Customize Your Sender Email (Optional)',
                Icon: MailIcon,
                content:
                    'Configure email communications to go out from your own domain. This increases trust and engagement with your end users.',
                button: {
                    text: 'Customize Email',
                    link: '/settings#account',
                },
            },
        ],
    },
};

export const SANDBOX_SETUP_STEPS = [
    {
        id: 1,
        title: 'Invite Your Team',
        icon: <UsersIcon />,
        content: commonSetupContent.inviteTeam,
        button: {
            text: 'Invite Your Team',
            link: '/settings#team',
        },
        completed: false,
    },
    // {
    //     id: 2,
    //     title: 'Try Out the Product',
    //     icon: <DiamondIcon />,
    //     content: commonSetupContent.tryOutProducts,
    //     button: {
    //         text: 'Try Out Sandbox',
    //         link: '#',
    //     },
    //     completed: false,
    // },
    {
        id: 2,
        title: 'Build and Run Workflows',
        icon: <ShieldIcon />,
        content: commonSetupContent.workflowSetup,
        button: {
            text: 'Create A Workflow',
            link: '/workflows',
        },
        completed: false,
    },
    {
        id: 3,
        title: 'Explore Integration Options',
        icon: <ConnectIcon />,
        content: 'Choose how you’d like to integrate:',
        subContent: commonSetupContent.integrationOptions,
    },
    {
        id: 4,
        title: 'Review System Behavior',
        icon: <SearchNoteIcon />,
        content:
            'Walk through TCM workflows in a sandbox state. Understand how your data will flow in production, but note that no actual records or alerts will be created.',
        button: {
            text: 'Switch to Production',
            link: '#',
            isOnClickHandler: true,
        },
    },
];
